import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { AvatarPicker, FormGrid, NextButton, Notice, OptionAutocomplete } from '../../../../ui';
import config from '../../../../config';
import noavatar from '../../../../public/images/noavatar.png';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { UserStatus } from '../../../../types/UserStatus';
import countryCodes from '../../../../util/options/countryCodes';

const mapStateToProps = (state) => {
  return {
    userUploadAvatarStatus: state.userUploadAvatarStatus,
    userSaveStatus: state.userSaveStatus,
    passwordChangeStatus: state.passwordChangeStatus,
    user: state.user,
    isMobile: state.resolution.device === 'mobile' || state.resolution.device === 'mobile-tablet',
    devices: state.devices,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data) => dispatch(actions.saveUser(data)),
    uploadAvatar: (avatarFile) => dispatch(actions.uploadAvatar(avatarFile)),
  };
};

const mandatoryFields = ['companyName', 'firstName', 'lastName', 'companyAddress', 'companyHqAddress', 'companyUrl', 'position'];

class Personal extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);

    this.state = {
      newAvatar: {},
      newAvatarError: '',
      errors: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.user.status !== prevProps.user.status &&
      this.props.userSaveStatus.status === OperationStatus.success
    ) {
      this.props.updateUser({ status: UserStatus.Active });
      this.props.history.push('/account');
    }
  }

  updateErrorState = (event, data) => {
    if (this.state.errors.includes(data.name)) {
      const errors = [...this.state.errors];
      errors.splice(this.state.errors.indexOf(data.name), 1);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    const user = { ...this.props.user };
    user[data.name] = data.value;

    return this.props.updateUser(user);
  };

  isSaveEnabled = () => {
    for (const el of mandatoryFields) {
      if (!this.props.user[el]) {
        return false;
      }
    }

    return true;
  };

  onAvatarUpload = ([acceptedImageFile]) => {
    const newAvatar = Object.assign(acceptedImageFile, {
      preview: URL.createObjectURL(acceptedImageFile),
    });

    this.setState({
      newAvatar,
      newAvatarError: '',
    });
  };

  onAvatarRejected = () => {
    this.setState({ newAvatarError: 'Avatar size can be no more than 1 Mb' });
  };

  onSave = async () => {
    const user = { ...this.props.user };
    const errors: any[] = [];

    mandatoryFields.forEach((el) => {
      if (!user[el]) {
        errors.push(el);
      }
    });

    if (errors.length) {
      return this.setState({ errors });
    }

    delete user.avatarUrl;

    if (Object.keys(this.state.newAvatar).length) {
      await this.props.uploadAvatar(this.state.newAvatar);
    }

    this.props.saveUser(user);
  };

  render() {
    const userSaveProcessing = this.props.userSaveStatus.status === OperationStatus.processing || this.props.userUploadAvatarStatus.status === OperationStatus.processing;
    const userSaveEnabled = this.isSaveEnabled();
    const user = this.props.user;
    const errors = this.state.errors;

    let avatarUrl;

    if (this.state.newAvatar.preview) {
      avatarUrl = this.state.newAvatar.preview;
    } else if (this.props.user.avatarUrl) {
      const href = new URL(this.props.user.avatarUrl, config.apiEndpoint).href;
      avatarUrl = href;
    } else {
      avatarUrl = noavatar;
    }

    // Adapter to the (event, { name, value }) handlers above; typing in a flagged field clears its error
    const change = (name: string, value: any) => {
      this.updateErrorState(null, { name });
      this.onChange(null, { name, value });
    };
    const text = (name: string, label: string) => (
      <TextField
        name={name}
        label={label}
        required={mandatoryFields.includes(name)}
        value={user[name] ?? ''}
        error={errors.includes(name)}
        onChange={(event) => change(name, event.target.value)}
        fullWidth
      />
    );

    return (
      <>
        <Notice title="Welcome!">Please fill the information below to complete your registration</Notice>

        <AvatarPicker
          src={avatarUrl}
          error={this.state.newAvatarError}
          onPick={(file) => this.onAvatarUpload([file])}
          onReject={this.onAvatarRejected}
        />

        <FormGrid isMobile={this.props.isMobile}>
          {text('firstName', 'First name')}
          {text('lastName', 'Last name')}
          <OptionAutocomplete
            label="Country"
            options={countryCodes}
            value={user.country}
            error={errors.includes('country')}
            onValueChange={(value) => change('country', value ?? '')}
          />
          {text('companyName', 'Company name')}
          {text('companyAddress', 'Company address')}
          {text('companyHqAddress', 'Company HQ address')}
          {text('position', 'Position')}
          {text('companyUrl', 'Company URL')}
        </FormGrid>

        <NextButton onClick={this.onSave} busy={userSaveProcessing} disabled={!userSaveEnabled} />
      </>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Personal));
